import { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Avatar, IconButton, styled, useTheme, Collapse, CircularProgress, Grow, Tooltip, Button } from "@mui/material";
import useModal from "@rf/base/modal/useModal";
import MxModalTemplate2 from "@rf-l1/modal/MxModalTemplate2";
import MxTypography from "@rf-l1/MxTypography";
import MxButton2 from "@rf-l1/buttons/MxButton2";
import { useController } from "react-hook-form";
import { CPropertyFormKeys } from "@rf/property/module/components/property-form/util/schemas/property-schema/propertySchema";
import {
  agentAutoCompleteItem,
  agentAutoCompleteItemAdapter
} from "@rf/adapters/autocomplete-items/agentItemAdapters";
import { IconChevronDown, IconChevronUp, IconGripVertical, IconSearch, IconTrash } from "@tabler/icons-react";
import { useSortableItems } from "@rf-l1/roma/useSortableItems";
import HCheckArray from "@rf-helper/check-have-value";
import { E_USERS_TYPES_ID } from "@/src-refactor/modules/shared/model/const/users/type.const";
import { useLazyGetAgentsQuery } from "@/src-refactor/modules/store/qrtk/services/users/users.qrtk";
import MxInputField from "@rf-l1/form/text-fields/MxInputField";
import MxImageBlur from "@rf-l1/images/MxImageBlur";

const AgentsSelectV2Modal = () => {
  const { onConfirm, onClose, getData } = useModal();
  const theme = useTheme()
  // Connect with RHF field
  const { control } = getData() as any;
  const { field } = useController({ name: CPropertyFormKeys.brokers_property, control });

  const [localAgents, setLocalAgents] = useState<agentAutoCompleteItem[]>(
    Array.isArray(field.value) ? [...field.value] : []
  );

  useEffect(() => {
    if (Array.isArray(field.value)) setLocalAgents([...field.value]);
  }, [JSON.stringify(field.value)]);

  // Add a single agent to the local selection (avoid duplicates)
  const handleAddAgent = (agent: agentAutoCompleteItem) => {
    setLocalAgents(prev => {
      if (prev.some(a => a.value === agent.value)) return prev;
      return [...prev, agent];
    });
  };

  const handleRemove = (id: number | string) => {
    setLocalAgents(prev => prev.filter(a => a.value !== id));
  };

  const moveItem = (from: number, to: number) => {
    setLocalAgents(prev => {
      const copy = [...prev];
      if (from < 0 || to < 0 || from >= copy.length || to >= copy.length) return copy;
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    });
  };

  const { getContentSortableProps, getSortableItemProps } = useSortableItems<agentAutoCompleteItem>({
    value: localAgents,
    direction: "vertical",
    onChange: (newValue) => setLocalAgents(newValue)
  });

  const handleSave = () => {
    field.onChange(localAgents);
    onConfirm?.(localAgents);
  };

  return (
    <MxModalTemplate2>
      <Grid container rowGap={2}>
        <Grid item xs={12}>
          <MxSelectAgentsFieldCustom
            selected={localAgents}
            onSelect={handleAddAgent}
          />
        </Grid>

        {localAgents.length > 0 && <Grid item xs={12}>
          <MxTypography variant="overline" color="primary.main" fontWeight={400}>
            Arrastra para reordenar
          </MxTypography>
        </Grid>}

        <Grid item xs={12}>
          {localAgents.length === 0 ? (
            <EmptyState>
              <div>
                <MxTypography variant="overline" color="grey.700" fontWeight={600} mb={1}>
                  No hay agentes seleccionados
                </MxTypography>
                <MxTypography variant="caption" color="grey.600">
                  Usa el buscador para agregar agentes
                </MxTypography>
              </div>
            </EmptyState>
          ) : (
            <Grid rowGap={1} {...getContentSortableProps()} height={250}>
              {localAgents.map((agent, index) => (
                <Row key={agent.value} {...getSortableItemProps(index)}>
                  <div className="drag">
                    <IconGripVertical size={18} />
                  </div>
                  <div className="order">
                    <MxTypography variant="overline" color="grey.600" fontWeight={600}>
                      {index + 1}
                    </MxTypography>
                  </div>
                  <Avatar
                    src={agent?.extraData?.agentData?.picture || ""}
                    alt={agent.label}
                    sx={{ width: 36, height: 36 }}
                  />
                  <div className="info">
                    <MxTypography variant="caption" fontWeight={600} color="grey.800">
                      {agent.label}
                    </MxTypography>
                    <MxTypography variant="overline" color="grey.600" fontWeight={400} lines={1}>
                      {agent?.extraData?.agentData?.role || ""}
                      {agent?.extraData?.agentData?.role && agent?.extraData?.agentData?.agencyName ? " • " : ""}
                      {agent?.extraData?.agentData?.agencyName || ""}
                    </MxTypography>
                  </div>
                  <div className="actions">
                    <Tooltip title="Subir">
                      <IconButton size="small" onClick={() => moveItem(index, Math.max(0, index - 1))}>
                        <IconChevronUp size={16} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Bajar">
                      <IconButton
                        size="small"
                        onClick={() => moveItem(index, Math.min(localAgents.length - 1, index + 1))}
                      >
                        <IconChevronDown size={16} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Eliminar">
                      <IconButton size="small" className="delete" onClick={() => handleRemove(agent.value)}>
                        <IconTrash size={16} color={theme.palette.error.light} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </Row>
              ))}
            </Grid>
          )}
        </Grid>

        <Grid item xs={12} container justifyContent="space-between" columnGap={2}>
          <Grid item>
            <MxButton2 onClick={onClose} textColor="error.main" variant="outlined">
              Cancelar
            </MxButton2>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={handleSave} sx={{ fontWeight: 400 }}>
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </MxModalTemplate2>
  );
};

export default AgentsSelectV2Modal;

interface MxSelectAgentsFieldCustomProps {
  selected: agentAutoCompleteItem[];
  onSelect: (agent: agentAutoCompleteItem) => void;
}
const MxSelectAgentsFieldCustom = (props: MxSelectAgentsFieldCustomProps) => {
  const theme = useTheme()
  const [GET_AGENTS, GET_AGENTS_DATA] = useLazyGetAgentsQuery()
  const ref = useRef('')

  const Items: agentAutoCompleteItem[] | [] = useMemo(() => {
    if (!HCheckArray(GET_AGENTS_DATA.data?.data)) return [];
    return GET_AGENTS_DATA.data!.data.map((agent, i) =>
      agentAutoCompleteItemAdapter({
        name: agent.name,
        id: agent.id,
        picture: agent.picture_url,
        agencyName: agent.agency.name,
        status: agent?.user_status,
        role: agent?.user_type?.name
      })
    );
  }, [GET_AGENTS_DATA.data?.data]);

  // Filter out already selected agents from the search results
  const filteredItems = useMemo(() => {
    if (!HCheckArray(Items)) return [] as agentAutoCompleteItem[];
    if (!HCheckArray(props.selected)) return Items as agentAutoCompleteItem[];
    const selectedIds = new Set(props.selected.map(a => a.value));
    return (Items as agentAutoCompleteItem[]).filter(i => !selectedIds.has(i.value));
  }, [Items, props.selected]);

  const onSearch = (search: string) => {
    GET_AGENTS({
      query: {
        q: search ?? "",
        page: 1,
        status: "active",
        type: [E_USERS_TYPES_ID.BROKER, E_USERS_TYPES_ID.JUNIOR, E_USERS_TYPES_ID.SENIOR, E_USERS_TYPES_ID.BROKER_MANAGER].join(",")
      }
    }).unwrap().then(() => {
      ref.current = search
    })
  }

  const loading = GET_AGENTS_DATA.status === "pending";


  return (
    <Grid container>

      <Grid item xs={12}>
        <MxInputField
          placeholder="Buscar agentes captadores"
          endIcon={loading ? <CircularProgress size={16} /> : <IconSearch />}
          onSearch={onSearch}
        />
      </Grid>

      {!loading && filteredItems.length === 0 && ref.current != '' && (
        <Grid item xs={12} color="black" mt={3} textAlign={'center'}>
          <MxTypography variant="overline" fontWeight={500} color='grey.700'>
            No hay resultados para la búsqueda "{ref.current}"
          </MxTypography>
        </Grid>
      )}

      {/* Animated results container */}
      <Collapse in={filteredItems.length > 0 && ref.current != ''} unmountOnExit timeout={250}>
        <Grid container item xs={12} maxHeight={180} overflow={'auto'} my={1} sx={{
          border: '1px solid #ccc',
          p: '0.5rem 0.4rem',
          borderRadius: '10px',
          rowGap: 1,
          backgroundColor: 'background.paper'
        }}>
          {filteredItems.map((x, idx) => (
            <Grow in={true} timeout={150 + idx * 30} key={x.id}>
              <Grid item xs={12} sx={{
                cursor: 'default', padding: '0.3rem 0.4rem', ':hover': {
                  background: theme.palette.grey[100],
                  borderRadius: 0.3
                }
              }}>
                <Grid
                  container
                  alignItems="center"
                  spacing={1}
                  sx={{
                    filter: x?.extraData?.agentData.isSuspend ? "grayscale(100%)" : "none"
                  }}
                >
                  <Grid item xs="auto">
                    <MxImageBlur
                      src={x.extraData?.agentData.picture}
                      width={30}
                      height={30}
                      borderRadius={30}
                      alt={x.extraData?.agentData.name}
                    />
                  </Grid>

                  <Grid container item flex={1} rowSpacing={0.1} columnSpacing={1} alignItems="center">
                    <Grid item xs={12}>
                      <MxTypography variant="caption" fontWeight={600} color="grey.800">
                        {x.extraData?.agentData.name}
                      </MxTypography>
                    </Grid>
                    <Grid item xs={'auto'}>
                      <MxTypography variant="overline" color="grey.600" fontWeight={400} lines={1}>
                        {x.extraData?.agentData.role}
                        {x.extraData?.agentData.role && x.extraData?.agentData.agencyName ? " • " : ""}
                        {x.extraData?.agentData.agencyName}
                      </MxTypography>
                    </Grid>
                  </Grid>

                  <Grid item xs="auto">
                    <Button size="small" variant="text" onClick={() => props.onSelect(x)}>
                      Seleccionar
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grow>
          ))}
        </Grid>
      </Collapse>
    </Grid>
  )


}



const Row = styled("div")(({ theme }) => ({
  display: "grid",
  width: '100%',
  alignItems: "center",
  gridTemplateColumns: "15px 5px 36px 1fr auto",
  gap: theme.spacing(1.5),
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 12,
  padding: theme.spacing(1),

  "& .drag": { color: theme.palette.grey[500], display: "flex", justifyContent: "center" },
  "& .order": { display: "flex", justifyContent: "center", alignItems: "center" },
  "& .info": { display: "flex", flexDirection: "column", rowGap: 3 },
  "& .actions": { display: "flex", alignItems: "center", gap: 8 },
  "& .actions .delete": { marginLeft: 6 },
  "& .chip": { marginLeft: theme.spacing(1) }
}));

const EmptyState = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 250,
  border: `1px dashed ${theme.palette.grey[300]}`,
  borderRadius: 12,
  background: theme.palette.background.paper,
  textAlign: "center",
  padding: theme.spacing(2)
}));
